import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import * as gptApi from '../../../api/gpt'
import Button from '../../components/form/Button'
import QuestionsWrapper from '../../components/gpt/QuestionsWrapper'
import QuestionCard from '../../components/gpt/QuestionCard'
import { useI18N } from '../../../libraries/i18n/hook'
import Loading from '../../components/layout/Loading'
import { MessageList, InProgressMessageBubble, AdvisorAvatar, ChatTextInput } from '../../components/gpt'
import { useAnalytics, ANALYTICS_EVENTS } from '../../../libraries/posthog'
import { AppError } from '../../../libraries/errors/AppError'
import MasterStudioAIPage, { STUDIO_AI_TABS } from './Master'
import PlusIcon from '../../../assets/icons/plus.svg?react'
import CalendarTimeIcon from '../../../assets/icons/calendar-time.svg?react'
import Card from '../../components/layout/Card'
import { useAllConversations, useConversation } from '../../../hocs/gpt/useConversation'
import ConversationHistory from '../../components/gpt/ConversationHistory'
import { AdvisorAvatarType, AdvisorMessage } from '../../../api/gpt/types'
import SideDrawer from '../../components/layout/SideDrawer'
import AdvisorAiFeedback from '../../containers/user-feedback/AdvisorAiFeedback'

const AdvisorAIPage = () => {
  const { t } = useI18N()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { trackEvent } = useAnalytics()
  const [showHistoricConversations, setShowHistoricConversations] = React.useState(false)
  const [answerInProgress, setAnswerInProgress] = React.useState<string>()
  const [loading, setLoading] = React.useState<boolean>(false)
  const {
    messages,
    conversationId,
    addMessage,
    clearConversation,
    setCurrentConversationId,
  } = useConversation()
  const { mutate: refreshConversation } = useAllConversations()

  React.useEffect(() => {
    if(!state?.question) return
    const question = state?.question
    window.history.replaceState({}, '')
    onSubmit(question)
  }, [])

  const handleClearConversation = () => {
    clearConversation()
    navigate('/advisor-ai', { replace: true })
  }

  const askGpt = async(question: string, currentMessages: AdvisorMessage[]) => {
    try {
      setLoading(true)
      trackEvent(ANALYTICS_EVENTS.ADVISOR_ASKED, { question })

      const apiMessages = [...currentMessages].map(msg => ({
        role: msg.role,
        content: msg.content,
        sources: msg.sources,
      }))

      // Get the streaming response
      const streamResponse = gptApi.askGpt(apiMessages, conversationId)

      if (!streamResponse) {
        throw new AppError('No response stream')
      }

      let streamingAnswer = ''     // Accumulated streaming content

      // Use the processStream utility to handle SSE
      for await (const chunk of streamResponse) {
        setLoading(false)
        streamingAnswer += chunk
        setAnswerInProgress(streamingAnswer)
      }

      refreshConversation()
    } catch (e: unknown) {
      addMessage({ content: t('advisor.notAvailable'), role: AdvisorAvatarType.ADVISOR }, currentMessages)
    } finally {
      setAnswerInProgress(undefined)
      setLoading(false)
    }
  }

  const onSubmit = async (question: string) => {
    if(!question || question === '') return
    const newMessages = addMessage({ content: question, role: AdvisorAvatarType.USER }, messages)
    askGpt(question, newMessages)
  }

  const NewChatButton = (variant: 'primary' | 'flat', size: 'sm' | 'lg') => (
    <Button
      iconOnlyBelow='lg'
      icon={<PlusIcon />}
      variant={variant}
      outlined
      onClick={handleClearConversation}
      size={size}
    >
      {t('advisor.newConversation')}
    </Button>
  )

  const HistoricConversationsButton = (
    <Button
      iconOnlyBelow='lg'
      icon={<CalendarTimeIcon />}
      variant='flat'
      outlined
      onClick={() => setShowHistoricConversations(true)}
      size='lg'
    />
  )

  const History = (
    <ConversationHistory
      onSelect={(id) => {
        setShowHistoricConversations(false)
        setCurrentConversationId(id)
      }}
    />
  )

  return (
    <MasterStudioAIPage activeTab={STUDIO_AI_TABS.ADVISOR_AI} buttons={[NewChatButton('flat', 'lg'), HistoricConversationsButton]}>
      {showHistoricConversations && <SideDrawer showCloseIcon onClose={() => setShowHistoricConversations(false)}>{History}</SideDrawer>}
      <div className='relative lg:px-8 rounded-md flex flex-col lg:flex-row min-h-[91vh] lg:min-h-[75vh] lg:h-[79vh]'>
        <div className='overflow-y-scroll hidden lg:block sticky top-0 lg:w-1/4 lg:mb-6 md:pe-10 md:py-3 rounded-md md:border-solid md:border-r-2 md:border-soft-green'>
          {History}
        </div>
        <div className='lg:w-3/4 md:pl-8 h-full contents lg:block md:h-[95%]'>
          <div className='hidden lg:block w-full text-end '>
            {NewChatButton('primary', 'sm')}
          </div>
          <div className='bg-white justify-end grow items-start flex flex-col h-full lg:px-0 lg:pb-6'>
            <div className='px-4 lg:px-0 overflow-y-scroll w-full'>
              <MessageList messages={messages} messageInProgress={answerInProgress && <InProgressMessageBubble message={answerInProgress} avatar={<AdvisorAvatar />} />}/>

              {loading && <div className='mx-auto pb-4 lg:pb-2 flex items-center justify-center'><Loading size='fill-container' /></div>}
              {messages.length === 0 && (
                <div className='mx-auto'>
                  <QuestionsWrapper>
                    <QuestionCard onClick={() => onSubmit(t('advisor.question1'))} question={t('advisor.question1')} />
                    <QuestionCard onClick={() => onSubmit(t('advisor.question2'))} question={t('advisor.question2')} />
                    <div className='hidden lg:block'><QuestionCard onClick={() => onSubmit(t('advisor.question3'))} question={t('advisor.question3')} /></div>
                  </QuestionsWrapper>
                </div>
              )}
              {conversationId && messages?.length > 1 && (
                <div className='max-w-2xl mx-auto h-auto bg-transparent w-full ms-auto'>
                  {<AdvisorAiFeedback msgIndex={messages?.length - 1} conversationId={conversationId} />}
                </div>
              )}
            </div>
            <ChatInput onSubmit={onSubmit} />
          </div>
        </div>
      </div>
    </MasterStudioAIPage>
  )
}

type ChatInputProps = { onSubmit: (question: string) => void }
const ChatInput: React.FC<ChatInputProps> = ({ onSubmit }) => {
  const [value, setValue] = React.useState<string>('')
  const { t } = useI18N()

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (value.trim()) {
      onSubmit(value)
      setValue('')
    }
  }

  return (
    <Card fullHeight={false} className='w-full max-w-2xl mx-auto sticky bottom-0 rounded-none sm:rounded-lg shadow-[0px_-4px_5px_0px_rgba(0,_0,_0,_0.1)] lg:shadow-md bg-white py-8 sm:py-2'>
      <ChatTextInput
        value={value}
        onChange={onChange}
        placeholder={t('advisor.whatDoYouNeed')}
        bgColor='gray-300'
        handleSubmit={handleSubmit}
      />
    </Card>
  )
}

export default AdvisorAIPage
